import React from 'react';
import { AutoBillingConfig, Client } from '../types';
import { 
  DocumentTextIcon, 
  CalendarIcon, 
  UserIcon,
  CurrencyEuroIcon,
  XMarkIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface AutoInvoicePreviewProps {
  config: AutoBillingConfig;
  client: Client;
  onClose: () => void;
}

export const AutoInvoicePreview: React.FC<AutoInvoicePreviewProps> = ({ 
  config, 
  client, 
  onClose 
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getNextBillingDate = () => {
    const today = new Date();
    const nextBilling = new Date(today.getFullYear(), today.getMonth(), config.billingDay);
    
    if (nextBilling <= today) {
      nextBilling.setMonth(nextBilling.getMonth() + 1);
    }
    
    return nextBilling;
  };

  const getPreviousMonth = () => {
    const today = new Date();
    const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return previousMonth.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  };

  const calculateInvoiceAmounts = () => {
    const subtotal = config.monthlyAmount;
    const ivaAmount = subtotal * (config.ivaPercentage / 100);
    const irpfAmount = subtotal * (config.irpfPercentage / 100);
    const total = subtotal + ivaAmount - irpfAmount;

    return { subtotal, ivaAmount, irpfAmount, total };
  };

  const { subtotal, ivaAmount, irpfAmount, total } = calculateInvoiceAmounts();
  const nextBillingDate = getNextBillingDate();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <DocumentTextIcon className="h-8 w-8 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Vista Previa - Factura Automática
              </h2>
              <p className="text-gray-600">Cliente: {client.name}</p>
              <div className="flex items-center space-x-2 mt-1">
                <ClockIcon className="h-4 w-4 text-orange-500" />
                <span className="text-sm text-orange-600 font-medium">
                  Próxima facturación: {formatDate(nextBillingDate)}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Invoice Content */}
        <div className="p-6">
          {/* Company Header */}
          <div className="text-center mb-8 pb-6 border-b border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Daniel Vázquez Facturación
            </h1>
            <p className="text-gray-600">Servicios profesionales de desarrollo web</p>
            <p className="text-gray-600">Calle Principal 123, Madrid</p>
            <p className="text-gray-600">daniel@facturacion.com | +34 600 000 000</p>
          </div>

          {/* Invoice Details */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
                Datos del Cliente
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium text-gray-900">{client.name}</p>
                {client.address && (
                  <p className="text-gray-600">{client.address}</p>
                )}
                {client.city && (
                  <p className="text-gray-600">{client.city}</p>
                )}
                {client.email && (
                  <p className="text-gray-600">{client.email}</p>
                )}
                {client.phone && (
                  <p className="text-gray-600">{client.phone}</p>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2 text-blue-600" />
                Detalles de la Factura
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Tipo:</span>
                  <span className="font-medium text-blue-600">Factura Automática</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Dominio:</span>
                  <span className="font-medium">{config.domain}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Próxima facturación:</span>
                  <span className="font-medium text-orange-600">{formatDate(nextBillingDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Día de facturación:</span>
                  <span className="font-medium">{config.billingDay} de cada mes</span>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Descripción del Servicio</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700">
                Mantenimiento web del dominio <strong>{config.domain}</strong> - {getPreviousMonth()}
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Incluye: Mantenimiento del servidor, actualizaciones de seguridad, 
                copias de seguridad, monitoreo 24/7 y soporte técnico.
              </p>
            </div>
          </div>

          {/* Service Details */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Detalle del Servicio</h3>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Concepto
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cantidad
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Precio Unitario
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subtotal
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      Mantenimiento web - {config.domain}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 text-right">
                      1
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 text-right">
                      {formatCurrency(config.monthlyAmount)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 text-right font-medium">
                      {formatCurrency(subtotal)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="ml-auto w-80">
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">IVA ({config.ivaPercentage}%):</span>
                <span className="font-medium">{formatCurrency(ivaAmount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">IRPF ({config.irpfPercentage}%):</span>
                <span className="font-medium text-red-600">-{formatCurrency(irpfAmount)}</span>
              </div>
              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between text-lg font-bold text-gray-900">
                  <span>TOTAL:</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Configuration Info */}
          <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Configuración de Facturación Automática</h4>
            <div className="grid grid-cols-2 gap-4 text-sm text-blue-800">
              <div>
                <span className="font-medium">Frecuencia:</span> Mensual
              </div>
              <div>
                <span className="font-medium">Día de facturación:</span> {config.billingDay} de cada mes
              </div>
              <div>
                <span className="font-medium">Estado:</span> 
                <span className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${
                  config.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {config.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div>
                <span className="font-medium">Próxima factura:</span> {formatDate(nextBillingDate)}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-6 border-t border-gray-200 text-center text-gray-600">
            <p>Esta es una factura automática generada por el sistema</p>
            <p className="text-sm mt-2">
              Se enviará automáticamente el {config.billingDay} de cada mes
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cerrar
          </button>
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Imprimir Vista Previa
          </button>
        </div>
      </div>
    </div>
  );
}; 